/* eslint-disable max-classes-per-file */
import { DESCRIBE } from '../monitoring';
import type { ErrorDescription } from '../monitoring';

/**
 * Sets an own non-enumerable property (like native `Error.cause`), so
 * Sentry-only fields stay out of `Object.keys`/`JSON.stringify` for partners.
 * Their class field declarations are type-only (stripped by Babel/tsc), so no enumerable slot is emitted.
 */
const setHidden = (target: object, key: string, value: unknown) => {
  Object.defineProperty(target, key, {
    value,
    writable: true,
    configurable: true,
    enumerable: false,
  });
};

interface HttpRequestInfo {
  method: string;
  path: string;
}

interface HttpErrorInit {
  /** HTTP status; absent when no response was received. */
  status?: number;
  request?: HttpRequestInfo;
  /** Parsed response body, when readable. */
  res?: unknown;
  cause?: unknown;
}

class HttpError extends Error {
  status?: number;

  request?: HttpRequestInfo;

  res?: unknown;

  cause?: unknown;

  constructor(
    message: string,
    { status, request, res, cause }: HttpErrorInit = {},
  ) {
    super(message);
    this.status = status;
    this.request = request;
    this.res = res;
    setHidden(this, 'cause', cause);
  }

  /** `res.errorType` from the backend error envelope. */
  get errorType(): string | undefined {
    const errorType = (this.res as { errorType?: unknown } | undefined)
      ?.errorType;
    return typeof errorType === 'string' ? errorType : undefined;
  }

  /** `res.errors` from the backend error envelope; `[]` when absent. */
  get errors(): string[] {
    const errors = (this.res as { errors?: unknown } | undefined)?.errors;
    return Array.isArray(errors) ? errors : [];
  }

  /** Status + endpoint for the Issue title; grouped per endpoint and status. */
  [DESCRIBE](): ErrorDescription {
    const endpoint = this.request
      ? `${this.request.method} ${this.request.path}`
      : undefined;
    const failure =
      this.status === undefined ? 'Network failure' : `HTTP ${this.status}`;
    return {
      detail: endpoint ? `${failure} ${endpoint}` : failure,
      grouping: [endpoint ?? 'unknown', String(this.status ?? 'none')],
      tags: endpoint ? { endpoint } : undefined,
    };
  }
}

class NetworkRequestFailedError extends HttpError {
  static message = 'NetworkRequestFailed';

  constructor(init?: HttpErrorInit) {
    super(NetworkRequestFailedError.message, init);
    this.name = 'NetworkRequestFailedError';
  }
}

class BrokerNotConnectedError extends HttpError {
  static message = 'BrokerNotConnected';

  constructor(init?: HttpErrorInit) {
    super(BrokerNotConnectedError.message, init);
    this.name = 'BrokerNotConnectedError';
  }
}

interface UnknownErrorInit {
  cause?: unknown;
  /** Free-form, non-sensitive context; sent as error context. */
  data?: Record<string, unknown>;
}

class UnknownError extends Error {
  cause?: unknown;

  data?: Record<string, unknown>;

  constructor(message: string, { cause, data }: UnknownErrorInit = {}) {
    super(message);
    this.name = 'UnknownError';
    this.data = data;
    setHidden(this, 'cause', cause);
  }
}

interface GatewayErrorOptions {
  code?: number | null;
  /** If `false` never reported as an Issue. */
  capture?: boolean;
  /** extra error context */
  data?: any;
  /** Underlying failure this error stands for */
  cause?: unknown;
}

class GatewayError extends Error {
  code?: number | null;

  data?: any;

  /** If `false` never reported as an Issue. */
  capture: boolean;

  /** Underlying failure this error stands for */
  cause?: unknown;

  /** Copy of `source`, with `overrides` applied. */
  constructor(source: GatewayError, overrides?: GatewayErrorOptions);

  /** Defaults: `code` null, `capture` true, `data` `{}`. */
  constructor(message: string, options?: GatewayErrorOptions);

  constructor(
    messageOrSource: string | GatewayError,
    options: GatewayErrorOptions = {},
  ) {
    super(
      typeof messageOrSource === 'string'
        ? messageOrSource
        : messageOrSource.message,
    );
    const source: GatewayErrorOptions =
      typeof messageOrSource === 'string'
        ? {}
        : {
            code: messageOrSource.code,
            capture: messageOrSource.capture,
            data: messageOrSource.data,
            cause: messageOrSource.cause,
          };
    const {
      code = null,
      capture = true,
      data = {},
      cause,
    } = { ...source, ...options };
    this.name = 'GatewayError';
    this.code = code;
    this.data = data;
    setHidden(this, 'capture', capture);
    if (cause !== undefined) setHidden(this, 'cause', cause);
  }

  /** Grouped per message + code. */
  [DESCRIBE](): ErrorDescription {
    return { grouping: [this.message, String(this.code ?? 'none')] };
  }
}

export type { HttpRequestInfo, GatewayErrorOptions, UnknownErrorInit };
export {
  HttpError,
  NetworkRequestFailedError,
  BrokerNotConnectedError,
  UnknownError,
  GatewayError,
};
